import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Search, 
  Receipt,
  Pencil, 
  Trash2,
  Building2,
  User,
  FileText,
  FileSignature,
  Calendar as CalendarIcon
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { transactionsAPI, propertiesAPI, clientsAPI } from '../lib/api';
import { 
  formatCurrency, 
  formatDate,
  transactionTypeLabels,
  paymentStatusLabels,
  getPaymentStatusColor
} from '../lib/utils';
import { toast } from 'sonner';

const initialFormData = {
  property_id: '',
  client_id: '',
  transaction_type: 'vente',
  amount: '',
  currency: 'XOF',
  commission_rate: '5',
  payment_status: 'en_attente',
  notes: '',
};

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isLeaseDatesOpen, setIsLeaseDatesOpen] = useState(false);
  const [leaseDatesForm, setLeaseDatesForm] = useState({ transaction_date: '', lease_end_date: '' });
  const [leaseDatesTransaction, setLeaseDatesTransaction] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  // Lease contract generation modal — lets the agent confirm/override the
  // monthly rent (and optionally charges + start date) before downloading the PDF
  const [isLeaseContractOpen, setIsLeaseContractOpen] = useState(false);
  const [leaseContractTx, setLeaseContractTx] = useState(null);
  const [leaseContractForm, setLeaseContractForm] = useState({
    monthly_rent: '',
    monthly_charges: '',
    start_date: '',
    persist: true,
  });
  const [generatingContract, setGeneratingContract] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [transRes, propRes, clientRes] = await Promise.all([
        transactionsAPI.getAll(),
        propertiesAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setTransactions(transRes.data);
      setProperties(propRes.data);
      setClients(clientRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (transaction = null) => {
    if (transaction) {
      setSelectedTransaction(transaction);
      setFormData({
        ...transaction,
        amount: transaction.amount.toString(),
        commission_rate: transaction.commission_rate.toString(),
      });
    } else {
      setSelectedTransaction(null);
      setFormData(initialFormData);
    }
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedTransaction(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.client_id || !formData.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        commission_rate: parseFloat(formData.commission_rate),
      };

      if (selectedTransaction) {
        await transactionsAPI.update(selectedTransaction.id, submitData);
        toast.success('Transaction mise à jour avec succès');
      } else {
        await transactionsAPI.create(submitData);
        toast.success('Transaction créée avec succès');
      }
      
      handleCloseForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTransaction) return;
    
    try {
      await transactionsAPI.delete(selectedTransaction.id);
      toast.success('Transaction supprimée avec succès');
      setIsDeleteOpen(false);
      setSelectedTransaction(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadLeaseContract = (transactionId) => {
    // Open the rent-confirmation modal first so the agent can verify the monthly rent
    const tx = transactions.find(t => t.id === transactionId);
    if (!tx) { toast.error('Transaction introuvable'); return; }
    if (tx.transaction_type !== 'location') { toast.error("Cette transaction n'est pas une location"); return; }
    const currentRent = tx.rental_details?.monthly_rent ?? tx.monthly_rent ?? tx.amount ?? '';
    const currentCharges = tx.rental_details?.monthly_charges ?? '';
    const currentStart = tx.transaction_date
      ? new Date(tx.transaction_date).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    setLeaseContractTx(tx);
    setLeaseContractForm({
      monthly_rent: currentRent ? String(currentRent) : '',
      monthly_charges: currentCharges ? String(currentCharges) : '',
      start_date: currentStart,
      persist: true,
    });
    setIsLeaseContractOpen(true);
  };

  const submitLeaseContract = async () => {
    if (!leaseContractTx) return;
    const rent = parseFloat(leaseContractForm.monthly_rent);
    if (!rent || rent <= 0) {
      toast.error('Le montant du loyer mensuel est requis');
      return;
    }
    setGeneratingContract(true);
    try {
      const params = {
        monthly_rent: rent,
        _t: Date.now(),
      };
      const charges = parseFloat(leaseContractForm.monthly_charges);
      if (!isNaN(charges) && charges >= 0) params.monthly_charges = charges;
      if (leaseContractForm.start_date) params.start_date = leaseContractForm.start_date;
      if (leaseContractForm.persist) params.persist = true;

      const response = await transactionsAPI.downloadLeaseContract(leaseContractTx.id, params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = leaseContractTx?.client_name
        ? leaseContractTx.client_name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40)
        : null;
      link.download = safeName
        ? `contrat_location_${safeName}_${leaseContractTx.id.slice(0, 8)}.pdf`
        : `contrat_location_${leaseContractTx.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(`Contrat téléchargé (loyer ${rent.toLocaleString('fr-FR')} ${leaseContractTx.currency || 'XOF'}/mois)`);
      setIsLeaseContractOpen(false);
      // Refresh to show updated rental_details if persisted
      if (leaseContractForm.persist) fetchData();
    } catch (error) {
      console.error('Error downloading lease contract:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du téléchargement du contrat');
    } finally {
      setGeneratingContract(false);
    }
  };

  // Signed contract upload
  const signedContractInputRef = useRef(null);
  const pendingSignedContractTxId = useRef(null);

  const triggerUploadSignedContract = (txId) => {
    pendingSignedContractTxId.current = txId;
    if (signedContractInputRef.current) {
      signedContractInputRef.current.value = '';
      signedContractInputRef.current.click();
    }
  };

  const handleSignedContractFile = async (e) => {
    const file = e.target.files?.[0];
    const txId = pendingSignedContractTxId.current;
    pendingSignedContractTxId.current = null;
    if (!file || !txId) return;
    try {
      await transactionsAPI.uploadSignedContract(txId, file);
      toast.success('Contrat signé téléversé. Visible par le locataire dans son espace.');
      fetchData();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('signed contract upload failed', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    }
  };


  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = 
      (transaction.property_title && transaction.property_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (transaction.client_name && transaction.client_name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = filterType === 'all' || transaction.transaction_type === filterType;
    const matchesStatus = filterStatus === 'all' || transaction.payment_status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Calculate commission preview
  const commissionPreview = formData.amount && formData.commission_rate
    ? parseFloat(formData.amount) * (parseFloat(formData.commission_rate) / 100)
    : 0;

  const openLeaseDates = (transaction) => {
    setLeaseDatesTransaction(transaction);
    setLeaseDatesForm({
      transaction_date: transaction.transaction_date ? transaction.transaction_date.split('T')[0] : '',
      lease_end_date: transaction.lease_end_date ? transaction.lease_end_date.split('T')[0] : ''
    });
    setIsLeaseDatesOpen(true);
  };

  const handleSaveLeaseDates = async () => {
    if (!leaseDatesTransaction) return;
    try {
      const payload = {};
      if (leaseDatesForm.transaction_date) payload.transaction_date = leaseDatesForm.transaction_date;
      if (leaseDatesForm.lease_end_date) payload.lease_end_date = leaseDatesForm.lease_end_date;
      else payload.lease_end_date = null;
      await transactionsAPI.updateLeaseDates(leaseDatesTransaction.id, payload);
      toast.success('Dates du bail mises a jour');
      setIsLeaseDatesOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      <input
        ref={signedContractInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={handleSignedContractFile}
        data-testid="signed-contract-file-input"
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Transactions
          </h1>
          <p className="text-gray-500 mt-1">
            {transactions.length} transaction{transactions.length > 1 ? 's' : ''} enregistrée{transactions.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button 
          onClick={() => handleOpenForm()}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="add-transaction-btn"
          disabled={properties.length === 0 || clients.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle transaction
        </Button>
      </div>

      {/* Warning if no properties or clients */}
      {(properties.length === 0 || clients.length === 0) && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <p className="text-yellow-800 text-sm">
              {properties.length === 0 && clients.length === 0 
                ? 'Vous devez d\'abord ajouter des biens et des clients pour créer des transactions.'
                : properties.length === 0 
                  ? 'Vous devez d\'abord ajouter des biens pour créer des transactions.'
                  : 'Vous devez d\'abord ajouter des clients pour créer des transactions.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher une transaction..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-transactions-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-transaction-type-select">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="vente">Vente</SelectItem>
                <SelectItem value="location">Location</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-payment-status-select">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="partiel">Partiel</SelectItem>
                <SelectItem value="complete">Complété</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      {filteredTransactions.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bien</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <div>
                          <span className="font-medium truncate max-w-[200px] block">
                            {transaction.property_title || 'Bien supprimé'}
                          </span>
                          {transaction.property_code && (
                            <span className="text-xs font-mono text-[#1B3A4B]">{transaction.property_code}</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <span>{transaction.client_name || 'Client supprimé'}</span>
                          {transaction.client_code && (
                            <span className="text-xs font-mono text-[#1B3A4B] block">{transaction.client_code}</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={transaction.transaction_type === 'vente' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'}>
                        {transactionTypeLabels[transaction.transaction_type]}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-[#1B3A4B]">
                      {formatCurrency(transaction.amount, transaction.currency)}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatCurrency(transaction.commission, transaction.currency)}
                      <span className="text-xs text-gray-400 ml-1">
                        ({transaction.commission_rate}%)
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPaymentStatusColor(transaction.payment_status)}>
                        {paymentStatusLabels[transaction.payment_status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      <div>
                        <span>{formatDate(transaction.created_at)}</span>
                        {transaction.transaction_type === 'location' && transaction.lease_end_date && (
                          <span className="block text-xs text-amber-600">
                            Fin: {formatDate(transaction.lease_end_date)}
                          </span>
                        )}
                        {transaction.transaction_type === 'location' && !transaction.lease_end_date && (
                          <span className="block text-xs text-gray-400">Duree indeterminee</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {transaction.transaction_type === 'location' && (
                          <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadLeaseContract(transaction.id)}
                            title="Telecharger le contrat de location"
                            data-testid={`download-contract-${transaction.id}`}
                          >
                            <FileText className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => triggerUploadSignedContract(transaction.id)}
                            title={transaction.signed_contract_url ? 'Contrat signé déjà téléversé - cliquez pour remplacer' : 'Téléverser le contrat signé'}
                            data-testid={`upload-signed-contract-${transaction.id}`}
                            className={transaction.signed_contract_url ? 'text-emerald-600 hover:text-emerald-700' : 'text-gray-500 hover:text-gray-700'}
                          >
                            <FileSignature className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openLeaseDates(transaction)}
                            title="Modifier les dates du bail"
                            data-testid={`lease-dates-${transaction.id}`}
                          >
                            <CalendarIcon className="w-4 h-4 text-amber-600" />
                          </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenForm(transaction)}
                          data-testid={`edit-transaction-${transaction.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setIsDeleteOpen(true);
                          }}
                          data-testid={`delete-transaction-${transaction.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune transaction trouvée</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                  ? 'Essayez de modifier vos filtres'
                  : 'Commencez par créer votre première transaction'}
              </p>
              {!searchQuery && filterType === 'all' && filterStatus === 'all' && properties.length > 0 && clients.length > 0 && (
                <Button 
                  onClick={() => handleOpenForm()}
                  className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle transaction
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction Form Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTransaction ? 'Modifier la transaction' : 'Nouvelle transaction'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="property_id">Bien immobilier *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(value) => {
                    const property = properties.find(p => p.id === value);
                    setFormData({ 
                      ...formData, 
                      property_id: value,
                      amount: property?.price?.toString() || formData.amount,
                      transaction_type: property?.transaction_type || formData.transaction_type,
                      currency: property?.currency || formData.currency
                    });
                  }}
                >
                  <SelectTrigger data-testid="transaction-property-select">
                    <SelectValue placeholder="Sélectionner un bien" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.filter(p => p.status === 'disponible' || p.id === selectedTransaction?.property_id).map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.title} - {formatCurrency(property.price, property.currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="client_id">Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger data-testid="transaction-client-select">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="transaction_type">Type de transaction *</Label>
                <Select
                  value={formData.transaction_type}
                  onValueChange={(value) => setFormData({ ...formData, transaction_type: value })}
                >
                  <SelectTrigger data-testid="transaction-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vente">Vente</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Montant *</Label>
                  <Input
                    id="amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="50000000"
                    data-testid="transaction-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="currency">Devise</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger data-testid="transaction-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                      <SelectItem value="EUR">Euro (EUR)</SelectItem>
                      <SelectItem value="USD">Dollar (USD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="commission_rate">Taux de commission (%)</Label>
                  <Input
                    id="commission_rate"
                    type="number"
                    step="0.1"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                    placeholder="5"
                    data-testid="transaction-commission-input"
                  />
                </div>
                <div>
                  <Label>Commission estimée</Label>
                  <div className="h-10 flex items-center px-3 bg-gray-50 rounded-md border text-[#1B3A4B] font-medium">
                    {formatCurrency(commissionPreview, formData.currency)}
                  </div>
                </div>
              </div>
              
              <div>
                <Label htmlFor="payment_status">Statut du paiement *</Label>
                <Select
                  value={formData.payment_status}
                  onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
                >
                  <SelectTrigger data-testid="transaction-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_attente">En attente</SelectItem>
                    <SelectItem value="partiel">Partiel</SelectItem>
                    <SelectItem value="complete">Complété</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes sur la transaction..."
                  rows={3}
                  data-testid="transaction-notes-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={saving}
                data-testid="transaction-submit-btn"
              >
                {saving ? 'Enregistrement...' : selectedTransaction ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette transaction ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La transaction sera définitivement supprimée et le statut du bien sera remis à "disponible".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-transaction-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lease Dates Modal */}
      <Dialog open={isLeaseDatesOpen} onOpenChange={setIsLeaseDatesOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Dates du bail</DialogTitle>
            <DialogDescription>
              {leaseDatesTransaction?.property_title} - {leaseDatesTransaction?.client_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Date de debut du bail</Label>
              <Input
                type="date"
                value={leaseDatesForm.transaction_date}
                onChange={e => setLeaseDatesForm(f => ({ ...f, transaction_date: e.target.value }))}
                data-testid="lease-start-input"
              />
            </div>
            <div>
              <Label>Date de fin du bail</Label>
              <Input
                type="date"
                value={leaseDatesForm.lease_end_date}
                onChange={e => setLeaseDatesForm(f => ({ ...f, lease_end_date: e.target.value }))}
                data-testid="lease-end-input"
              />
              {!leaseDatesForm.lease_end_date && (
                <p className="text-xs text-gray-400 mt-1">Vide = duree indeterminee</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLeaseDatesOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveLeaseDates} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="save-lease-dates">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lease Contract Generation Modal (with monthly rent confirmation) */}
      <Dialog open={isLeaseContractOpen} onOpenChange={setIsLeaseContractOpen}>
        <DialogContent className="max-w-md" data-testid="lease-contract-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#D4A745]" />
              Générer le contrat de location
            </DialogTitle>
            <DialogDescription>
              Confirmez le <b>montant du loyer mensuel</b> avant de télécharger le contrat.
              Cette valeur apparaîtra dans l&apos;article&nbsp;3 du contrat.
            </DialogDescription>
          </DialogHeader>
          {leaseContractTx && (
            <div className="space-y-3 py-2">
              <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 border border-gray-100">
                <div><b>Locataire :</b> {leaseContractTx.client_name || '—'}</div>
                <div><b>Bien :</b> {leaseContractTx.property_title || properties.find(p => p.id === leaseContractTx.property_id)?.title || '—'}</div>
              </div>

              <div>
                <Label htmlFor="lc-monthly-rent">Loyer mensuel ({leaseContractTx.currency || 'XOF'}) *</Label>
                <Input
                  id="lc-monthly-rent"
                  type="number"
                  min="0"
                  step="500"
                  value={leaseContractForm.monthly_rent}
                  onChange={(e) => setLeaseContractForm({ ...leaseContractForm, monthly_rent: e.target.value })}
                  className="text-lg font-bold tabular-nums"
                  placeholder="Ex: 75000"
                  data-testid="lc-monthly-rent"
                  autoFocus
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  C&apos;est le montant payable chaque mois (hors charges).
                </p>
              </div>

              <div>
                <Label htmlFor="lc-monthly-charges">Charges mensuelles ({leaseContractTx.currency || 'XOF'})</Label>
                <Input
                  id="lc-monthly-charges"
                  type="number"
                  min="0"
                  step="500"
                  value={leaseContractForm.monthly_charges}
                  onChange={(e) => setLeaseContractForm({ ...leaseContractForm, monthly_charges: e.target.value })}
                  placeholder="Ex: 5000 (optionnel)"
                  data-testid="lc-monthly-charges"
                />
              </div>

              <div>
                <Label htmlFor="lc-start-date">Date de prise d&apos;effet</Label>
                <Input
                  id="lc-start-date"
                  type="date"
                  value={leaseContractForm.start_date}
                  onChange={(e) => setLeaseContractForm({ ...leaseContractForm, start_date: e.target.value })}
                  data-testid="lc-start-date"
                />
              </div>

              <label className="flex items-start gap-2 text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={leaseContractForm.persist}
                  onChange={(e) => setLeaseContractForm({ ...leaseContractForm, persist: e.target.checked })}
                  data-testid="lc-persist"
                />
                <span>
                  <b>Enregistrer ces valeurs</b> sur le bail (recommandé). Les loyers, quittances et avis d&apos;échéance utiliseront automatiquement ce montant.
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLeaseContractOpen(false)} disabled={generatingContract}>
              Annuler
            </Button>
            <Button
              onClick={submitLeaseContract}
              disabled={generatingContract || !leaseContractForm.monthly_rent}
              className="bg-[#D4A745] hover:bg-[#B8902F] text-white"
              data-testid="lc-generate-btn"
            >
              {generatingContract ? 'Génération...' : 'Télécharger le contrat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transactions;
